import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Row, Col, Form, Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
//api
import { companySelector } from 'slices/company';
// components
import Button from 'components/backoffice/buttonCustom';
import CkEditorComponent from 'components/backoffice/inputEditor/InputEditorComponent';
import CustomInput from 'components/backoffice/customInput';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const LegalForm = ({
  fetchApi,
  createApi,
  updateApi,
  titleKey,
  textKey,
  title,
  dataTestId
}) => {
  const { control, setValue, handleSubmit, reset } = useForm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [borderClasses, setBorderClasses] = useState({});
  const { t } = useTranslation(['backoffice', 'footer']);
  const [openAccordion, setOpenAccordion] = useState(null);

  const company = useSelector(companySelector);
  const languages = company.languages;
  const borderError = 'border border-danger';

  const handleAccordionToggle = (languageCode) => {
    setOpenAccordion(prevOpen => (prevOpen === languageCode ? null : languageCode));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchApi();
        if (response.status === 200 && response.data.length > 0) {
          setData(response.data[0]);
        } else {
          setIsCreating(true);
        }
      } catch (error) {
        toast.error(`${t('error')}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (data) {
      languages.forEach(lang => {
        const titleLangKey = `${titleKey}_${lang.code_iso2}`;
        const textLangKey = `${textKey}_${lang.code_iso2}`;
        setValue(titleLangKey, data[titleLangKey] || '');
        setValue(textLangKey, data[textLangKey] || '');
      });
    } else {
      reset();
    }
  }, [data, languages, setValue, reset]);

  const onSubmit = async (formData) => {
    const borderClassesState = {};
    const isAnyPairFilled = languages.some(lang => {
      const titleLangKey = `${titleKey}_${lang.code_iso2}`;
      const textLangKey = `${textKey}_${lang.code_iso2}`;
      const isTitleEmpty = !formData[titleLangKey];
      const isTextEmpty = !formData[textLangKey];
      if (isTitleEmpty && !isTextEmpty) {
        borderClassesState[titleLangKey] = borderError;
      } else if (!isTitleEmpty && isTextEmpty) {
        borderClassesState[textLangKey] = borderError;
      } else if (isTitleEmpty && isTextEmpty) {
        borderClassesState[titleLangKey] = borderError;
        borderClassesState[textLangKey] = borderError;
      }
      return (formData[titleLangKey] && formData[textLangKey]);
    });

    if (!isAnyPairFilled) {
      toast.error(`${t('error')}: ${t('completeAtLeastOneField')}`, { theme: 'colored' });
      setBorderClasses(borderClassesState);
    } else {
      try {
        setBorderClasses('')
        const dataToSend = new FormData();
        dataToSend.append('title', formData[`${titleKey}_es`] || '');
        dataToSend.append('text', formData[`${textKey}_es`] || '');
        languages.forEach(lang => {
          const code = lang.code_iso2;
          dataToSend.append(`${titleKey}_${code}`, formData[`${titleKey}_${code}`] || '');
          dataToSend.append(`${textKey}_${code}`, formData[`${textKey}_${code}`] || '');
        });

        setLoading(true);
        if (isCreating) {
          const response = await createApi(dataToSend);
          if (response.status === 201) {
            toast.success(t('creadocorrectamente'));
            setData(response.data);
            setIsCreating(false);
          } else {
            toast.error(`${t('error')}: ${t('completeFieldsLegalSettings')}`, { theme: 'colored' });
          }
        } else {
          const response = await updateApi(data.id, dataToSend);
          if (response.status === 200) {
            toast.success(t('editadocorrectamente'));
          } else {
            toast.error(`${t('error')}: ${t('mensajeerror')}`, { theme: 'colored' });
          }
        }
      } catch (error) {
        toast.error(`${t('error')}: ${t('mensajeerror')}`, { theme: 'colored' });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <h1 className="text-light text-start mb-4">{title}</h1>
      <div className='backoffice-accordion'>
        <Form onSubmit={handleSubmit(onSubmit)} data-testid={dataTestId}>
          <div className="card-faqs-container">
            <Row className="mx-1 w-100">
              {languages.map(lang => (
                <React.Fragment key={lang.code_iso2}>
                  <Accordion
                    className="mt-3 card-faqs"
                    activeKey={openAccordion === lang.code_iso2 ? "0" : null}
                  >
                    <Accordion.Item eventKey="0" className="bg-transparent border-0">
                      <Accordion.Header
                        data-testid={`header ${lang.nombre}`}
                        className="text-light card-faqs-header mt-0"
                        onClick={() => handleAccordionToggle(lang.code_iso2)}
                      >
                        {lang.nombre}
                      </Accordion.Header>
                      <Accordion.Body>
                        <div className="card-faqs-line mb-2" />
                        <div className="d-flex flex-row">
                          <div className="card-faqs-item">
                            <Form.Group className="mb-3">
                              <Controller
                                name={`${titleKey}_${lang.code_iso2}`}
                                control={control}
                                render={({ field }) => (
                                  <CustomInput
                                    dataTestId={`titulo ${lang.nombre}`}
                                    type="text"
                                    value={field.value}
                                    label={`${t('titulo')}`}
                                    placeholder={`${t('titulo')}`}
                                    className={borderClasses[`${titleKey}_${lang.code_iso2}`] || ''}
                                    onChange={(e) => {
                                      field.onChange(e);
                                    }}
                                    required
                                  />
                                )}
                              />
                            </Form.Group>
                          </div>
                          <div className="card-faqs-item">
                            <Form.Group className="mb-3">
                              <Form.Label className="text-light">{`${t('text')}`}</Form.Label>
                              {openAccordion === lang.code_iso2 && (
                                <Controller
                                  name={`${textKey}_${lang.code_iso2}`}
                                  control={control}
                                  render={({ field }) => (
                                    <div className={borderClasses[`${textKey}_${lang.code_iso2}`] || ''}>
                                      <CkEditorComponent
                                        dataTestId={`text ${lang.nombre}`}
                                        value={field.value}
                                        onChange={(e) => {
                                          field.onChange(e);
                                        }}
                                      />
                                    </div>
                                  )}
                                />
                              )}
                            </Form.Group>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </React.Fragment>
              ))}
            </Row>
          </div>
          <Row className="mb-4">
            <Col sm={12}>
              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button
                  color="primary"
                  type="submit"
                  className='btn-intra  bg-primary-color btn btn-primary width-btn-save'
                  disabled={loading}
                >
                  {loading ?
                    <SpinnerComponent loading={loading} /> :
                    isCreating ? t('crear') : t('actualizar')
                  }
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default LegalForm;
